import {
  model,
  Model,
  prop,
  modelAction,
  modelFlow,
  _async,
  _await,
  registerRootStore,
  Frozen,
  frozen
} from "mobx-keystone";
import { computed } from "mobx";
import { toast } from "../../ui/toast";
import { createContext } from "react";
import {
  APP_PAGES,
  AppPageType,
  BOOTSTRAP_SCRIPT_ID,
  fromQueryString,
  setQueryString,
  removeQueryString,
  invoiceFromQueryString,
  publicTokenFromPath
} from "../helpers/constants";
import { refundAddressError } from "../helpers/chainAddress";
import {
  sendDirectiveAndDataToRailsController,
  sendToRailsController,
  deleteFromRailsController
} from "../../helpers/requests";
import {
  AccountInfo,
  ApiTokenInfo,
  ApiUsageInfo,
  AppBootstrap,
  AssetInfo,
  EnvironmentInfo,
  LandingInfo,
  RateInfo,
  SwapOrderPayload
} from "../helpers/types";
import { SwapOrder } from "./SwapOrder";
import { NewSwapQuote } from "./NewSwapQuote";
import { ApiToken } from "./ApiToken";
import { startOrderActionCable } from "../helpers/actionCable";

@model("lightningswap/AppWorkspace")
export class AppWorkspace extends Model({
  hydrated:     prop<boolean>(false),
  openPage:     prop<AppPageType>(APP_PAGES.NEW_SWAP),

  account:      prop<Frozen<AccountInfo> | null>(null),
  apiTokens:    prop<ApiToken[]>(() => []),
  apiUsage:     prop<Frozen<ApiUsageInfo> | null>(null),
  swapOrders:   prop<SwapOrder[]>(() => []),
  rates:        prop<Frozen<RateInfo[]>>(() => frozen<RateInfo[]>([])),
  assets:       prop<Frozen<AssetInfo[]>>(() => frozen<AssetInfo[]>([])),
  newSwap:      prop<NewSwapQuote>(() => new NewSwapQuote({})),
  environment:  prop<Frozen<EnvironmentInfo> | null>(null),
  landing:      prop<Frozen<LandingInfo> | null>(null),

  selectedOrderId:   prop<number | null>(null),
  publicToken:       prop<string | null>(null),
  // Mirrors the server's rule (AccountData.can_list_swaps?). Navigation only —
  // the server does not send history to anyone this is false for.
  canListSwaps:      prop<boolean>(false),
  areWeWaitingOnRails: prop<boolean>(false)
}) {
  @computed
  get email(): string {
    return this.account?.data?.email ?? "";
  }

  // Logged out. Swapping still works; only API keys and history need an account.
  @computed
  get isGuest(): boolean {
    return this.account?.data == null;
  }

  // Set when viewing /o/<token>, so a guest can act on the swap they hold.
  @computed
  get viewedOrder(): SwapOrder | undefined {
    if (!this.publicToken) return undefined;
    return this.swapOrders.find((o) => o.publicToken === this.publicToken);
  }

  @computed
  get activeApiTokens(): ApiToken[] {
    return this.apiTokens.filter((t) => t.isActive);
  }

  @computed
  get selectedOrder(): SwapOrder | undefined {
    if (this.selectedOrderId == null) return undefined;
    return this.swapOrders.find((o) => o.id === this.selectedOrderId);
  }

  @modelAction
  setPage = (page: AppPageType) => {
    // Navigation cannot open a page this visitor is not entitled to — the
    // swaps list falls back to the single-swap lookup.
    const target = this.canOpen(page)
      ? page
      : page === APP_PAGES.SWAPS
      ? APP_PAGES.FIND_SWAP
      : APP_PAGES.NEW_SWAP;

    // "New swap" always means a blank form — including when already on that page.
    if (target === APP_PAGES.NEW_SWAP) this.newSwap.reset();

    // The prefilled invoice only describes the page they landed on; once they
    // navigate, a stale ?invoice= would refill the form on the next refresh.
    removeQueryString("invoice");

    this.openPage = target;
    // Leaving /o/<token> must drop the public URL — history and new-swap live
    // under "/", and keeping the token path would re-open this swap on refresh.
    if (this.publicToken && target !== APP_PAGES.FIND_SWAP) {
      this.publicToken = null;
      window.history.pushState({}, "", `/?page=${target}`);
      return;
    }
    setQueryString("page", target);
  };

  @modelAction
  setWaitingOnRails = (v: boolean) => {
    this.areWeWaitingOnRails = v;
  };

  @modelAction
  selectOrder = (orderId: number | null) => {
    this.selectedOrderId = orderId;
  };

  // Push /o/<token> into the address bar and subscribe to that swap's channel.
  // Always the single-swap page — never the history table, even with an API key.
  @modelAction
  goToPublicOrder = (token: string) => {
    this.publicToken = token;
    this.openPage = APP_PAGES.FIND_SWAP;
    window.history.pushState({}, "", `/o/${token}`);
    startOrderActionCable(this, token);
  };

  @modelAction
  hydrateDataFromPage = () => {
    const scriptEl = document.getElementById(BOOTSTRAP_SCRIPT_ID) as HTMLScriptElement | null;
    if (!scriptEl || !scriptEl.textContent) {
      console.warn("Missing __app_bootstrap JSON");
      return;
    }
    let data: AppBootstrap;
    try {
      data = JSON.parse(scriptEl.textContent);
    } catch (err) {
      console.error(err, { tags: { where: "hydrateDataFromPage" } });
      return;
    }

    this.account     = data.account ? frozen(data.account) : null;
    this.apiUsage    = frozen(data.api_usage);
    this.rates       = frozen(data.rates);
    this.assets      = frozen(data.assets);
    this.environment = frozen(data.environment);
    this.landing     = data.landing ? frozen(data.landing) : null;
    this.setApiTokens(data.api_tokens);
    this.swapOrders  = data.swap_orders.map((o) => SwapOrder.fromPayload(o));
    this.canListSwaps = data.can_list_swaps === true;
    this.publicToken = publicTokenFromPath();

    const viewed = this.viewedOrder;
    if (viewed) this.selectedOrderId = viewed.id;

    // A shared "?invoice=" link lands on a form already holding that invoice;
    // NewSwapPage's parse effect picks it up from there.
    const sharedInvoice = invoiceFromQueryString();
    if (sharedInvoice && !this.publicToken) this.newSwap.setBolt11(sharedInvoice);

    this.openPage = this.resolveInitialPage();
    this.hydrated = true;
  };

  // Pages this visitor may open. The swaps LIST is history, so it needs an
  // account with an API key; everyone else looks up one swap at a time by code.
  @computed
  get allowedPages(): AppPageType[] {
    const pages: AppPageType[] = [APP_PAGES.NEW_SWAP, APP_PAGES.FIND_SWAP];
    if (this.canListSwaps) pages.push(APP_PAGES.SWAPS);
    if (!this.isGuest) pages.push(APP_PAGES.API_TOKENS, APP_PAGES.SETTINGS);
    return pages;
  }

  canOpen(page: AppPageType): boolean {
    return this.allowedPages.includes(page);
  }

  // /o/<token> is always one swap (FindSwapPage). History lives only at
  // /?page=swaps for accounts with an API key, and is never a landing page —
  // bare "/" always opens the new-swap form, signed in or not.
  private resolveInitialPage = (): AppPageType => {
    if (this.publicToken) return APP_PAGES.FIND_SWAP;

    // A pay link is a request to pay that invoice — it outranks any ?page=.
    if (invoiceFromQueryString()) return APP_PAGES.NEW_SWAP;

    const pageParam = fromQueryString("page") as AppPageType | null;
    if (pageParam && this.canOpen(pageParam)) return pageParam;

    // ?page=swaps without permission lands on the lookup, not an empty table.
    if (pageParam === APP_PAGES.SWAPS) return APP_PAGES.FIND_SWAP;

    return APP_PAGES.NEW_SWAP;
  };

  @modelAction
  setApiTokens = (tokens: ApiTokenInfo[]) => {
    this.apiTokens = tokens.map((t) => ApiToken.fromInfo(t));
  };

  @modelAction
  upsertApiToken = (token: ApiTokenInfo) => {
    const existing = this.apiTokens.find((t) => t.uuid === token.uuid);
    if (existing) {
      existing.label = token.label;
      existing.publicKey = token.public_key;
      existing.secret = token.secret;
      existing.lastUsedAt = token.last_used_at;
      existing.revokedAt = token.revoked_at ?? null;
      return;
    }
    this.apiTokens.unshift(ApiToken.fromInfo(token));
  };

  @modelAction
  setApiUsage = (usage: ApiUsageInfo) => {
    this.apiUsage = frozen(usage);
  };

  @modelAction
  upsertSwapOrder = (payload: SwapOrderPayload) => {
    const idx = this.swapOrders.findIndex((o) => o.id === payload.id);
    // Stale cable race: a late order-update with paying_invoice must not
    // overwrite payout-settled → completed already applied to the store.
    if (idx >= 0 && this.swapOrders[idx].phase === "completed" && payload.phase !== "completed") {
      return;
    }
    const order = SwapOrder.fromPayload(payload);
    if (idx >= 0) {
      this.swapOrders[idx] = order;
    } else {
      this.swapOrders.unshift(order);
    }
  };

  @modelAction
  markDepositSeen = (orderId: number, data: {
    deposit_tx_id?: string;
    deposit_tx_amount?: string;
    deposit_confirmations?: number;
    phase?: string;
  }) => {
    const order = this.swapOrders.find((o) => o.id === orderId);
    if (!order) return;
    if (data.deposit_tx_id) order.depositTxId = data.deposit_tx_id;
    if (data.deposit_tx_amount) order.depositTxAmount = data.deposit_tx_amount;
    if (data.deposit_confirmations != null) order.depositConfirmations = data.deposit_confirmations;
    if (data.phase) order.phase = data.phase;
  };

  @modelAction
  markPayoutSettled = (orderId: number, data: {
    payout_tx_id?: string;
    phase?: string;
  }) => {
    const order = this.swapOrders.find((o) => o.id === orderId);
    if (!order) return;
    if (data.payout_tx_id) order.payoutTxId = data.payout_tx_id;
    order.phase = data.phase ?? "completed";
  };

  @modelAction
  markOrderEmergency = (orderId: number, data: Partial<SwapOrderPayload>) => {
    const order = this.swapOrders.find((o) => o.id === orderId);
    if (!order) return;
    if (data.phase) order.phase = data.phase;
    if (data.emergency_status) order.emergencyStatus = data.emergency_status;
    if (data.emergency_choice) order.emergencyChoice = data.emergency_choice;
    if (data.emergency_repeat != null) order.emergencyRepeat = data.emergency_repeat;
    if (data.attention != null) order.attention = data.attention;
    if (data.attention_reason !== undefined) order.attentionReason = data.attention_reason;
    if (data.refund_reason !== undefined) order.refundReason = data.refund_reason;
    if (data.refund_amount !== undefined) order.refundAmount = data.refund_amount;
  };

  showError = (message: string) => {
    toast.show({ tone: "error", title: "Error", message });
  };

  showSuccess = (message: string) => {
    toast.show({ tone: "success", title: "Success", message });
  };

  @modelFlow
  parseInvoice = _async(function* (this: AppWorkspace) {
    const q = this.newSwap;
    const invoice = q.bolt11.trim();
    if (!invoice) return;

    q.setParsingInvoice(true);
    try {
      const resp = yield* _await(
        sendDirectiveAndDataToRailsController(
          "parse_invoice",
          { bolt11: invoice },
          "/app/directive_and_data"
        )
      );
      if (q.bolt11.trim() !== invoice) return;
      if (resp.success === false) {
        q.setInvoiceResult(invoice, null, null, resp.message ?? "Invalid Lightning invoice.");
        return;
      }
      const description =
        typeof resp.description === "string" && resp.description.trim()
          ? resp.description.trim()
          : null;
      q.setInvoiceResult(invoice, resp.amount_sats, resp.expires_at, null, description);
    } catch (err) {
      console.error(err, { tags: { class: "AppWorkspace", function: "parseInvoice" } });
      if (q.bolt11.trim() === invoice) {
        q.setInvoiceResult(invoice, null, null, "Could not parse the invoice.");
      }
    } finally {
      if (q.bolt11.trim() === invoice) q.setParsingInvoice(false);
    }
  });

  @modelFlow
  fetchQuote = _async(function* (this: AppWorkspace) {
    const q = this.newSwap;
    if (!q.asset || !q.invoiceSats || q.parsedBolt11 !== q.bolt11.trim()) return;

    q.setQuoting(true);
    try {
      const resp = yield* _await(
        sendDirectiveAndDataToRailsController(
          "quote_swap",
          {
            from_code:    q.asset,
            bolt11:       q.bolt11
          },
          "/app/directive_and_data"
        )
      );
      if (resp.success === false && !resp.deposit_amount) {
        q.setQuoteResult(null, resp.errors ?? [resp.message ?? "Quote failed"]);
        return;
      }
      q.setQuoteResult(resp.deposit_amount ?? null, resp.errors ?? []);
    } catch (err) {
      console.error(err, { tags: { class: "AppWorkspace", function: "fetchQuote" } });
      q.setQuoteResult(null, ["Quote request failed"]);
    } finally {
      q.setQuoting(false);
    }
  });

  @modelFlow
  createSwap = _async(function* (this: AppWorkspace) {
    const q = this.newSwap;
    if (!q.asset) {
      this.showError("Choose a pay-in asset.");
      return;
    }
    if (!q.invoiceSats || q.parsedBolt11 !== q.bolt11.trim()) {
      this.showError("Enter a valid, unexpired Lightning invoice first.");
      return;
    }

    this.setWaitingOnRails(true);
    try {
      const resp = yield* _await(
        sendDirectiveAndDataToRailsController(
          "create_swap",
          {
            type:         "fixed",
            direction:    "to",
            from:         q.asset,
            to:           "BTCLN",
            bolt11:       q.bolt11
          },
          "/app/directive_and_data"
        )
      );
      if (resp.success === false) {
        this.showError(resp.message ?? "Could not create swap.");
        if (resp.errors?.length) q.setQuoteResult(null, resp.errors);
        return;
      }
      if (resp.order) {
        this.upsertSwapOrder(resp.order);
        this.selectOrder(resp.order.id);
        q.reset();
        // Move the browser to the swap's own URL: for a guest this link is the
        // only way back to it, so it must exist before they can navigate away.
        this.goToPublicOrder(resp.order.public_token);
        this.showSuccess("Swap created — save this page's link to track it.");
      }
    } catch (err) {
      console.error(err, { tags: { class: "AppWorkspace", function: "createSwap" } });
      this.showError("Could not create swap.");
    } finally {
      this.setWaitingOnRails(false);
    }
  });

  @modelFlow
  submitRefund = _async(function* (
    this: AppWorkspace,
    order: SwapOrder,
    refundAddress: string,
    refundMemo?: string
  ) {
    // Fail fast on the wrong-network paste instead of a server round-trip.
    // The server revalidates (and checks the checksum) regardless.
    const addressError = refundAddressError(order.fromCode, refundAddress);
    if (addressError) {
      this.showError(addressError);
      return;
    }
    this.setWaitingOnRails(true);
    try {
      const resp = yield* _await(
        sendDirectiveAndDataToRailsController(
          "submit_refund",
          {
            order_id:       order.providerOrderId,
            // Guests are authorized by the swap's public token; owners are
            // matched on order_id server-side.
            public_token:   order.publicToken,
            // Required by ApiRefunds — a missing choice is rejected, never
            // defaulted, so a malformed request cannot move money.
            choice:         "REFUND",
            refund_address: refundAddress.trim(),
            address:        refundAddress.trim(),
            tag:            refundMemo?.trim() || null
          },
          "/app/directive_and_data"
        )
      );
      if (resp.success === false) {
        this.showError(resp.message ?? "Refund submission failed.");
        return;
      }
      if (resp.order) this.upsertSwapOrder(resp.order);
      this.showSuccess("Refund address submitted.");
    } catch (err) {
      console.error(err, { tags: { class: "AppWorkspace", function: "submitRefund" } });
      this.showError("Refund submission failed.");
    } finally {
      this.setWaitingOnRails(false);
    }
  });

  @modelFlow
  createApiToken = _async(function* (this: AppWorkspace, label: string) {
    const trimmed = label.trim() || "default";
    this.setWaitingOnRails(true);
    try {
      const resp = yield* _await(
        sendToRailsController({ label: trimmed }, "/api_tokens")
      );
      if (resp.success === false) {
        this.showError(resp.message ?? "Could not create API token.");
        return;
      }
      if (resp.token) {
        this.upsertApiToken(resp.token);
        this.showSuccess("API token created.");
      }
    } catch (err) {
      console.error(err, { tags: { class: "AppWorkspace", function: "createApiToken" } });
      this.showError("Could not create API token.");
    } finally {
      this.setWaitingOnRails(false);
    }
  });

  @modelFlow
  revokeApiToken = _async(function* (this: AppWorkspace, token: ApiToken) {
    this.setWaitingOnRails(true);
    try {
      const resp = yield* _await(
        deleteFromRailsController(`/api_tokens/${token.uuid}`)
      );
      if (resp.success === false) {
        this.showError(resp.message ?? "Could not revoke token.");
        return;
      }
      token.revoke();
      this.showSuccess("API token revoked.");
    } catch (err) {
      console.error(err, { tags: { class: "AppWorkspace", function: "revokeApiToken" } });
      this.showError("Could not revoke token.");
    } finally {
      this.setWaitingOnRails(false);
    }
  });
}

export const runCreateAppWorkspace = (): AppWorkspace => {
  const ws = new AppWorkspace({});
  registerRootStore(ws);
  return ws;
};

const createdWorkspace = runCreateAppWorkspace();
export const AppWorkspaceContext = createContext<AppWorkspace>(createdWorkspace);
